'use client';
import { MultiSelect, Radio, Select, TagsInput, Textarea, TextInput } from '@mantine/core';

import { Button } from '@scaleup/frontend/ui';
import {
  legalOrganizationStatus,
  primaryBusinessActivity,
  STATES_AND_TERRITORIES,
  Supplier,
  SupplierData,
  certificationTypes
} from '@scaleup/models';
import {
  IconArrowRight,
  IconBriefcase,
  IconBuildings,
  IconMapPin,
  IconUsers,
} from '@tabler/icons-react';
import { FormSection, IntakeBase } from './common';
import { useSupplierIntakeContext } from './context';
import { useState } from 'react';
import { set } from 'lodash';

export const Details = () => {
  const { supplier, setCurrentStep, detailsForm, updateSupplier, lastStep, setLastStep } =
    useSupplierIntakeContext();
  const [loading, setLoading] = useState(false);
  const handleClick = async () => {
    const res = detailsForm.validate();
    if (res.hasErrors) {
      return;
    }

    if(!detailsForm.isDirty()) {
      if(lastStep === 'review') {
        setLastStep('details');
        setCurrentStep('review');
      }
      else {
        setLastStep('details');
        setCurrentStep('demographics');
      }
      
    }
    const supplierData: SupplierData = {
      ...supplier.supplierData,
      description: detailsForm.values.description,
      industry: detailsForm.values.industry,
      primaryIndustrySector: detailsForm.values.primaryIndustrySector,
      productsAndServices: detailsForm.values.productsAndServices,
      establishmentYear: detailsForm.values.establishmentYear,
      geographiesServed: detailsForm.values.geographiesServed,
      multipleSites: detailsForm.values.multipleSites,
      primaryBusinessActivity: detailsForm.values.primaryBusinessActivity,
      legalOrganizationStatus: detailsForm.values.legalOrganizationStatus,
      primaryShareholder: detailsForm.values.primaryShareholder,
      demographicDesignations: detailsForm.values.demographicDesignations,
      mainContact: {
        firstName: detailsForm.values.mainContactFirstName,
        lastName: detailsForm.values.mainContactLastName,
        title: detailsForm.values.mainContactTitle,
        email: detailsForm.values.mainContactEmail,
        phone: detailsForm.values.mainContactPhone,
      },
      keyContact1: {
        firstName: detailsForm.values.keyContact1FirstName,
        lastName: detailsForm.values.keyContact1LastName,
        title: detailsForm.values.keyContact1Title,
        email: detailsForm.values.keyContact1Email,
        phone: detailsForm.values.keyContact1Phone,
      },
      keyContact2: {
        firstName: detailsForm.values.keyContact2FirstName,
        lastName: detailsForm.values.keyContact2LastName,
        title: detailsForm.values.keyContact2Title,
        email: detailsForm.values.keyContact2Email,
        phone: detailsForm.values.keyContact2Phone,
      },
    };

    const updatedSupplier: Supplier = {
      ...supplier,
      supplierData: supplierData,
      address1: detailsForm.values.address1,
      address2: detailsForm.values.address2,
      city: detailsForm.values.city,
      state: detailsForm.values.state,
      zipCode: detailsForm.values.zipCode,
      contactEmail: detailsForm.values.contactEmail,
      contactPhone: detailsForm.values.contactPhone,
    };
    setLoading(true);
    updateSupplier(updatedSupplier).then((newSupplier: Supplier) => {
      setLoading(false);
      console.log('Updated Supplier:', newSupplier);
      if(lastStep === 'review') {
        setLastStep('details');
        setCurrentStep('review');
      }
      else {
        setLastStep('details');
        setCurrentStep('demographics');
      }
    });
    // add api call
  };
  return (
    <IntakeBase currentStep={0}>
      <div className="flex flex-col gap-2">
        <p className="text-text-primary font-bold text-xl">
          Review Your Company Information
        </p>
        <p className="text-text-secondary">
          We've pre-filled some of your company details. Please review and
          confirm their accuracy before moving forward.
        </p>
      </div>

      <div className="rounded-[1rem] bg-primary-25 p-8 my-8 w-full">
        <div className="flex gap-4 items-center">
          <div className="w-[2.5rem] h-[2.5rem] rounded-full flex items-center justify-center bg-primary-50">
            <IconBuildings color="#4eb3cf" />
          </div>

          <p className="text-text-primary text-xl font-bold">
            {supplier.orgName}
          </p>
        </div>

        <FormSection
          leftPadding
          withDivider
          icon={<IconMapPin color="#4eb3cf" />}
        >
          <div className="flex flex-col gap-4">
            <div className="flex gap-4 w-2/3">
              <div className="w-1/2">
                <TextInput
                  label="Address Line 1"
                  required
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.address1}
                  {...detailsForm.getInputProps('address1')}
                />
              </div>
              <div className="w-1/2">
                <TextInput
                  label="Address Line 2"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.address2}
                  {...detailsForm.getInputProps('address2')}
                />
              </div>
            </div>
            <div className="flex gap-4 w-2/3">
              <div className="w-1/2">
                <TextInput
                  label="City"
                  required
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.city}
                  {...detailsForm.getInputProps('city')}
                />
              </div>
              <div className="w-1/4">
                <Select
                  label="State"
                  required
                  classNames={{
                    input: 'w-full',
                  }}
                  data={STATES_AND_TERRITORIES.map((it) => ({
                    value: it.name,
                    label: it.name,
                  }))}
                  error={detailsForm.errors.state}
                  {...detailsForm.getInputProps('state')}
                />
              </div>
              <div className="w-1/4">
                <TextInput
                  type="number"
                  required
                  label="Zip Code"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.zipCode}
                  {...detailsForm.getInputProps('zipCode')}
                />
              </div>
            </div>
            <div className="flex gap-4 w-2/3">
              <div className="w-1/2">
                <TextInput
                  label="Contact Email"
                  required
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.contactEmail}
                  {...detailsForm.getInputProps('contactEmail')}
                />
              </div>
              <div className="w-1/2">
                <TextInput
                  label="Contact Phone"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.contactPhone}
                  {...detailsForm.getInputProps('contactPhone')}
                />
              </div>
            </div>
            <div className="flex gap-4 w-2/3">
              <div className="w-1/2">
                <TextInput
                  label="Website"
                  required
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.website}
                  {...detailsForm.getInputProps('website')}
                />
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection
          leftPadding
          withDivider
          icon={<IconBriefcase color="#4eb3cf" />}
        >
          <div className="flex flex-col gap-4 w-full">
            <div className="flex gap-4 w-full">
              <div className="w-1/2">
                <TextInput
                  label="Industry"
                  required
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.industry}
                  {...detailsForm.getInputProps('industry')}
                />
              </div>
              <div className="w-1/2">
                <TextInput
                  label="Primary IndustrySector"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.primaryIndustrySector}
                  {...detailsForm.getInputProps('primaryIndustrySector')}
                />
              </div>
            </div>
            <div className=" w-full">
              <Textarea
                label="Company Description"
                required
                autosize
                minRows={5}
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={detailsForm.errors.description}
                {...detailsForm.getInputProps('description')}
              />
            </div>

            <div className=" w-full">
              <TagsInput
                label="Products & Services"
                required
                classNames={{ input: 'w-full', wrapper: 'w-full' }}
                error={detailsForm.errors.productsAndServices}
                {...detailsForm.getInputProps('productsAndServices')}
              />
            </div>

            <div className="flex gap-4 w-full">
              <div className="w-1/2">
                <TextInput
                  label="Company Establishment Year"
                  type="number"
                  required
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.establishmentYear}
                  {...detailsForm.getInputProps('establishmentYear')}
                />
              </div>
              <div className="w-1/2">
                <TagsInput
                  required
                  label="Locations Served (States, Regions, or Countries)"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.geographiesServed}
                  {...detailsForm.getInputProps('geographiesServed')}
                />
              </div>
            </div>

            <div className="flex gap-4 w-full">
              <div className="w-1/2">
                <Radio.Group
                  label="Does the company have multiple sites?"
                  required
                  error={detailsForm.errors.multipleSites}
                  {...detailsForm.getInputProps('multipleSites')}
                >
                  <div className="flex gap-8">
                    <Radio value="true" label="Yes" />
                    <Radio value="false" label="No" />
                  </div>
                </Radio.Group>
              </div>
              <div className="w-1/2">
                <Select
                  required
                  label="Primary Business Activity"
                  classNames={{ input: 'w-full' }}
                  data={primaryBusinessActivity}
                  error={detailsForm.errors.primaryBusinessActivity}
                  {...detailsForm.getInputProps('primaryBusinessActivity')}
                />
              </div>
            </div>

            <div className="flex gap-4 w-full">
              <div className="w-1/2">
                <Select
                  required
                  label="Legal Organization Status"
                  classNames={{ input: 'w-full' }}
                  data={legalOrganizationStatus}
                  error={detailsForm.errors.legalOrganizationStatus}
                  {...detailsForm.getInputProps('legalOrganizationStatus')}
                />
              </div>
              <div className="w-1/2">
                <TextInput
                  label="Name of Primary Shareholder"
                  required
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.primaryShareholder}
                  {...detailsForm.getInputProps('primaryShareholder')}
                />
              </div>
            </div>
            <div className="flex gap-4 w-full">
              <div className="w-1/2">
                <MultiSelect
                  label="Demographic Designations"
                  classNames={{ input: 'w-full' }}
                  data={certificationTypes['Demographic Certification'].filter((type) => type !== 'None' && type !== 'Other')}
                  {...detailsForm.getInputProps('demographicDesignations')}
                />
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection leftPadding icon={<IconUsers color="#4eb3cf" />}>
          <div className="flex flex-col gap-4 w-full">
            <p className="text-text-primary font-bold">Main Contact</p>
            <div className="flex gap-4 w-2/3">
              <div className="w-1/2">
                <TextInput
                  label="First Name"
                  required
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.mainContactFirstName}
                  {...detailsForm.getInputProps('mainContactFirstName')}
                />
              </div>
              <div className="w-1/2">
                <TextInput
                  label="Last Name"
                  required
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.mainContactLastName}
                  {...detailsForm.getInputProps('mainContactLastName')}
                />
              </div>
            </div>

            <div className="flex gap-4 w-2/3">
              <div className="w-1/2">
                <TextInput
                  label="Title"
                  required
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.mainContactTitle}
                  {...detailsForm.getInputProps('mainContactTitle')}
                />
              </div>

              <div className="w-1/2">
                <TextInput
                  label="Email"
                  required
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.mainContactEmail}
                  {...detailsForm.getInputProps('mainContactEmail')}
                />
              </div>
            </div>
            <div className="flex gap-4 w-2/3">
              <div className="w-1/2">
                <TextInput
                  label="Phone"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.maincontactPhone}
                  {...detailsForm.getInputProps('mainContactPhone')}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 w-full pt-6">
            <p className="text-text-primary font-bold">Key Contact 1</p>
            <div className="flex gap-4 w-2/3">
              <div className="w-1/2">
                <TextInput
                  label="First Name"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.keyContact1FirstName}
                  {...detailsForm.getInputProps('keyContact1FirstName')}
                />
              </div>
              <div className="w-1/2">
                <TextInput
                  label="Last Name"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.keyContact1LastName}
                  {...detailsForm.getInputProps('keyContact1LastName')}
                />
              </div>
            </div>

            <div className="flex gap-4 w-2/3">
              <div className="w-1/2">
                <TextInput
                  label="Title"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.keyContact1Title}
                  {...detailsForm.getInputProps('keyContact1Title')}
                />
              </div>

              <div className="w-1/2">
                <TextInput
                  label="Email"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.keyContact1Email}
                  {...detailsForm.getInputProps('keyContact1Email')}
                />
              </div>
            </div>
            <div className="flex gap-4 w-2/3">
              <div className="w-1/2">
                <TextInput
                  label="Phone"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.keyContact1Phone}
                  {...detailsForm.getInputProps('keyContact1Phone')}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 w-full pt-6">
            <p className="text-text-primary font-bold">Key Contact 2</p>
            <div className="flex gap-4 w-2/3">
              <div className="w-1/2">
                <TextInput
                  label="First Name"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.keyContact2FirstName}
                  {...detailsForm.getInputProps('keyContact2FirstName')}
                />
              </div>
              <div className="w-1/2">
                <TextInput
                  label="Last Name"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.keyContact2LastName}
                  {...detailsForm.getInputProps('keyContact2LastName')}
                />
              </div>
            </div>

            <div className="flex gap-4 w-2/3">
              <div className="w-1/2">
                <TextInput
                  label="Title"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.keyContact2Title}
                  {...detailsForm.getInputProps('keyContact2Title')}
                />
              </div>

              <div className="w-1/2">
                <TextInput
                  label="Email"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.keyContact2Email}
                  {...detailsForm.getInputProps('keyContact2Email')}
                />
              </div>
            </div>
            <div className="flex gap-4 w-2/3">
              <div className="w-1/2">
                <TextInput
                  label="Phone"
                  classNames={{ input: 'w-full' }}
                  error={detailsForm.errors.keyContact2Phone}
                  {...detailsForm.getInputProps('keyContact2Phone')}
                />
              </div>
            </div>
          </div>
        </FormSection>
      </div>

      <div className="flex justify-end">
        <Button
          text="Continue"
          variant={'primary'}
          suffixIcon={<IconArrowRight />}
          onClick={handleClick}
          loading={loading}
        />
      </div>
    </IntakeBase>
  );
};
